import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import ReorderableList, {ItemData} from 'ui-library/reorderable/ReorderableList';
import ReorderableSimpleTableRow, {
  ReorderableSimpleTableRowProps,
} from 'ui-library/reorderable/ReorderableSimpleTableRow';

import {AddComponentButton, RemoveComponentButton} from '../RunSchemaEditorActionButton';

export interface SortColumnsModalProps {
  initialColumns: Array<string>;
  possibleColumns: Array<string>;
  onRequestSubmit: (columns: Array<string>) => void;
  onRequestClose: () => void;
}

interface ColumnItemProps {
  availableColumns: Array<string>;
  columnValue: string;
  onChange: (value: string) => void;
  onRemove: () => void;
}

const ColumnItem = (props: ColumnItemProps): JSX.Element => (
  <div className={css(styles.itemContainer)}>
    <div className={css(styles.itemDropdown)}>
      <SelectDropdown
        displayName={props.columnValue}
        menu={
          <Menu value={props.columnValue} width={Menu.WIDTHS.FULL}>
            {props.availableColumns.map((column) => (
              <Option
                key={column}
                value={column}
                onSelect={() => {
                  props.onChange(column);
                }}
              >
                {column}
              </Option>
            ))}
          </Menu>
        }
      />
    </div>
    <RemoveComponentButton
      label="Remove"
      className={css(styles.itemButton)}
      onClick={() => {
        props.onRemove();
      }}
      dataTestAttrs={{
        component: 'SortColumnsModal',
        element: 'removeColumn',
        key: props.columnValue,
      }}
    />
  </div>
);

export default function SortColumnsModal(props: SortColumnsModalProps): JSX.Element {
  const [columns, setColumns] = useState(props.initialColumns);
  const availableColumns = props.possibleColumns.filter((column) => !columns.includes(column));

  const _getListItemForColumn = (column: string, idx: number): ItemData<ReorderableSimpleTableRowProps> => ({
    id: column,
    itemClass: ReorderableSimpleTableRow,
    itemProps: {
      isDraggingDisabled: false,
      rowContainerClassName: css(styles.listItemContainer),
      rowClassName: css(styles.listItem),
      dragHandleClassName: css(styles.dragHandle),
      row: {
        id: column,
        cells: [
          {
            contentsClassName: css(styles.contents),
            key: column,
            contents: (
              <ColumnItem
                availableColumns={availableColumns}
                columnValue={column}
                onChange={(value: string) => {
                  const newColumns = [...columns];
                  newColumns[idx] = value;
                  setColumns(newColumns);
                }}
                onRemove={() => {
                  const newColumns = [...columns];
                  newColumns.splice(idx, 1);
                  setColumns(newColumns);
                }}
              />
            ),
          },
        ],
      },
    },
  });

  return (
    <Modal
      buttons={[
        {
          style: ButtonStyle.SECONDARY,
          submit: true,
          text: 'Submit',
        },
      ]}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        props.onRequestSubmit(columns);
      }}
      title="Configure input file sorting"
    >
      <div className={css(styles.titleContainer)}>
        <div className={css(styles.titleText)}>Column</div>
        <AddComponentButton
          label="Add a new column sorting condition"
          disabled={availableColumns.length === 0}
          onClick={() => {
            setColumns([...columns, availableColumns[0]]);
          }}
          dataTestAttrs={{
            component: 'SortColumnsModal',
            element: 'addColumn',
          }}
        />
      </div>
      {columns.length > 0 && (
        <ReorderableList
          itemDatas={columns.map((column, idx) => _getListItemForColumn(column, idx))}
          onMoveItem={(updatedColumns: Array<string>) => {
            setColumns(updatedColumns);
          }}
          onMoveItemEnd={(column: string) => {}}
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  itemContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContents: 'flex-start',
  },
  itemDropdown: {
    flex: 0.9,
  },
  itemButton: {
    flex: 0.1,
    marginRight: '-24px',
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: '16px',
  },
  titleText: {
    marginTop: '-1px',
  },
  listItemContainer: {
    marginLeft: '-16px',
  },
  listItem: {
    padding: '10px 14px 10px 0',
  },
  contents: {
    marginRight: 0,
  },
  dragHandle: {
    marginRight: 0,
    width: '16px',
  },
});
